import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { defer, EMPTY, from, Observable, of } from "rxjs"
import { catchError, delayWhen, filter, switchMap } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import * as uuid from "uuid"
import { flatten, head, last } from "ramda"
import { compact } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    ADD_NEW_PAGE_REQUESTED,
    ADD_NEW_SCHEMATIZED_TILE_REQUESTED,
    ADD_NEW_TAB_CLICKED,
    addSchematizedGrid,
    addSchematizedLayout,
    addSchematizedTile,
    CLOSE_CUSTOM_FOLDER_FORM_CLICKED,
    DELETE_CURRENT_PAGE_REQUESTED,
    DELETE_SCHEMATIZED_TILE_REQUESTED,
    DELETE_TAB_CLICKED,
    deleteSchematizedGrid,
    deleteSchematizedGridGroup,
    deleteSchematizedLayout,
    deleteSchematizedTile,
    deleteSchematizedTileRequested,
    ENTER_FOLDER_CLICKED,
    FETCH_SCHEMATIZED_DATA_REQUESTED,
    fetchSchematizedFailure,
    fetchSchematizedSuccess,
    MARK_TILE_AS_STICKY_CLICKED,
    MOVE_SCHEMATIZED_TILE_REQUESTED,
    SCHEMATIZED_AGENDA_MOUNTED,
    SCHEMATIZED_CANCEL_BUTTON_CLICKED,
    SCHEMATIZED_LAYOUT_DELETE_MUTATION_REQUESTED,
    SCHEMATIZED_LAYOUT_RENAME_CLICKED,
    SCHEMATIZED_LAYOUT_RENAME_CONFIRMED,
    SCHEMATIZED_SAVE_BUTTON_CLICKED,
    SCHEMATIZED_SAVE_FAILURE,
    SCHEMATIZED_SAVE_MUTATION_REQUESTED,
    SCHEMATIZED_SAVE_SUCCESS,
    SCHEMATIZED_UPDATE_PAGES_REQUESTED,
    schematizedFetchDataRequested,
    schematizedGridSelected,
    schematizedLayoutDeleteMutationRequested,
    schematizedLayoutSelected,
    schematizedRenameLayoutValueChanged,
    schematizedRenameLayoutValueReset,
    schematizedSaveFailure,
    schematizedSaveMutationRequested,
    schematizedSaveSuccess,
    UPDATE_CUSTOM_FOLDER,
    updateSchematizedGridGroup,
    updateSchematizedGroups,
    updateSchematizedLayout,
    updateSchematizedTile,
} from "./actions"
import { isSchematizedAgendaOpened } from "../../../data/selectors"
import { waitForLocationsLoaded } from "../../reports/customers/customersTable/epics"
import type { StateObject } from "../../../redux/types"
import { Action } from "../../../redux/types"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    getCurrentSchematizedGroups,
    getSchematizedComponentData,
    getSchematizedLayouts,
    getSchematizedRenameLayoutValue,
    getSchematizedSelectedGridGroup,
    getSchematizedSelectedGridId,
    getSchematizedSelectedLayoutData,
    getSchematizedSelectedLayoutId,
    getSchematizedStateObject,
    getSelectedSchematizedResolution,
    isPlaceOccupied,
    isSelectedGridInRoot,
} from "./selectors"
import { createLoadingEnded, createLoadingStarted } from "../../../filters/actions"
import { CATEGORY_UPDATED } from "../../categories/itemsCategories/actions"
import { getSelectedLocationId } from "../../../common/locationsPicker/selectors"
import { LOCATION_SELECTED } from "../../../common/locationsPicker/actions"
import { Router } from "../../../../server/router"
import { fetchSchematizedData, schematizedLayoutDeleteApi, schematizedSaveApi } from "./request"
import { getHttpHeaders } from "../../../auth/selectors"
import type { SchematizedData, SchematizedPage, SchematizedTile } from "../../../../graphql/types"
import {
    getChangedLayouts,
    getFirstLayoutId,
    getUpdatedSchematizedGroups,
    removeOrphanedSchematizedGroups,
} from "./utils"
import { getItemsAndCategoriesListResponse } from "../../categories/selectors"
import { StickyTypeEnum } from "./types"
import { NotificationType, openNotification } from "../../../uiKit/Notification/Notification"
import { logger } from "../../../../utils/loggerWrap"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"

export const fetchSchematizedRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_AGENDA_MOUNTED, ACCOUNT_SELECTED, REFETCH_DATA, LOCATION_SELECTED),
        delayWhen(action => waitForLocationsLoaded(actions$, deps.getState(), action)),
        filter(() => isSchematizedAgendaOpened(deps.getState())),
        filter(() => !!getSelectedLocationId(deps.getState())),
        switchMap(() => [schematizedFetchDataRequested(), createLoadingStarted("schematized-view")])
    )

export const fetchSchematizedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SCHEMATIZED_DATA_REQUESTED),
        switchMap(() =>
            defer(() =>
                fetchSchematizedData(getHttpHeaders(deps.getState()), getSelectedLocationId(deps.getState()))
            ).pipe(
                switchMap((response: Maybe<SchematizedData[]>) => {
                    if (response) {
                        return [
                            createLoadingEnded("schematized-view"),
                            fetchSchematizedSuccess(removeOrphanedSchematizedGroups(response)),
                        ]
                    } else {
                        return [
                            createLoadingEnded("schematized-view"),
                            fetchSchematizedFailure({
                                status: 404,
                            }),
                        ]
                    }
                }),
                catchError(error => {
                    logger.error("fetchSchematizedEpics - fetchSchematizedData error", error)
                    return from([fetchSchematizedFailure(error), createLoadingEnded("schematized-view")])
                })
            )
        )
    )

export const customFolderUpdatedEpics = (
    actions$: Observable<any>,
    _state$: Observable<any>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(UPDATE_CUSTOM_FOLDER),
        switchMap((action: Action) => {
            return [
                updateSchematizedTile(action.payload.tile.id, {
                    customFolder: {
                        name: action.payload.tile.name,
                        icon: action.payload.tile.icon,
                        color: action.payload.tile.color,
                    },
                }),
            ]
        })
    )

export const schematizedCustomFolderCloseClickEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(CLOSE_CUSTOM_FOLDER_FORM_CLICKED, UPDATE_CUSTOM_FOLDER, CATEGORY_UPDATED),
        filter(() => isSchematizedAgendaOpened(deps.getState())),
        switchMap(() => {
            Router.push("/sales-input/schematized")
            return []
        })
    )

export const schematizedUpdatePagesRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_UPDATE_PAGES_REQUESTED),
        switchMap((action: Action) => {
            const currentGridGroup = getSchematizedSelectedGridGroup(deps.getState())

            if (!currentGridGroup) {
                logger.error("schematizedUpdatePagesRequestedEpics - no currentGridGroup")
                return []
            }

            return [
                updateSchematizedGridGroup(currentGridGroup.id, {
                    pages: action.payload.newPages,
                }),
            ]
        })
    )

export const schematizedMoveTileEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MOVE_SCHEMATIZED_TILE_REQUESTED),
        switchMap((action: Action) => {
            const schematizedUiData = getSchematizedComponentData(deps.getState())
            const isOccupied = isPlaceOccupied(
                schematizedUiData,
                action.payload.x,
                action.payload.y,
                action.payload.gridId
            )
            return isOccupied
                ? EMPTY
                : [
                      updateSchematizedTile(action.payload.tileId, {
                          x: action.payload.x,
                          y: action.payload.y,
                      }),
                  ]
        })
    )

export const schematizedMarkAsStickyClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MARK_TILE_AS_STICKY_CLICKED),
        switchMap((action: Action) => {
            const isInRoot = isSelectedGridInRoot(deps.getState())

            if (action.payload.stickyType === StickyTypeEnum.Root && !isInRoot) {
                const schematizedGroups = getCurrentSchematizedGroups(deps.getState())
                const tileData: Maybe<SchematizedTile> = head(
                    compact(
                        flatten(
                            schematizedGroups.map(g =>
                                g.pages.map(p => p.tiles.map(t => (t.id === action.payload.tile.id ? t : null)))
                            )
                        )
                    )
                )
                const rootGridPage = head(compact(schematizedGroups.map(g => g.isRoot && head(g.pages))))
                const rootGridId = rootGridPage ? rootGridPage.id : null

                if (!rootGridId || !rootGridPage || !tileData) {
                    logger.error("schematizedMarkAsStickyClickedEpics - could not found rootGrid or tileData")
                    return []
                }

                const collidingTile = rootGridPage.tiles.find(
                    t => t.x === action.payload.tile.x && t.y === action.payload.tile.y
                )
                return [
                    deleteSchematizedTile(action.payload.tile.id),
                    addSchematizedTile({ ...tileData, isSticky: true }, rootGridId),
                    ...(collidingTile && collidingTile.id ? [deleteSchematizedTile(collidingTile.id)] : []),
                ]
            } else {
                return [
                    updateSchematizedTile(action.payload.tile.id, {
                        isSticky: !action.payload.tile.isSticky,
                    }),
                ]
            }
        })
    )

export const saveSchematizedStartLoading = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_SAVE_MUTATION_REQUESTED, SCHEMATIZED_LAYOUT_DELETE_MUTATION_REQUESTED),
        switchMap(() => [createLoadingStarted("schematized-view")])
    )

export const saveSchematizedEndLoading = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_SAVE_SUCCESS, SCHEMATIZED_SAVE_FAILURE),
        switchMap(() => [createLoadingEnded("schematized-view")])
    )

export const schematizedSaveButtonClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) => {
    return actions$.pipe(
        ofType(SCHEMATIZED_SAVE_BUTTON_CLICKED),
        switchMap(() => {
            const locationId: Maybe<string> = getSelectedLocationId(deps.getState())

            if (!locationId) {
                logger.error("schematizedSaveButtonClickedEpics - locationId is null")
                return []
            }

            const itemsAndCats = getItemsAndCategoriesListResponse(deps.getState())?.data ?? []

            const oldLayouts = getSchematizedStateObject(deps.getState()).oldSchematizedLayouts
            const newLayouts = getSchematizedLayouts(deps.getState()).map((l, idx) => ({
                ...l,
                // getSchematizedLayouts are sorted by sortIndex.
                // Saving by array index will keep the sortIndexes start from 0
                sortIndex: idx,
                // Do not save tiles with non-existing products/items
                data: l.data.map(p => ({
                    ...p,
                    pages: p.pages.map(page => ({
                        ...page,
                        tiles: page.tiles.filter(tile => {
                            if (
                                tile.categoryId &&
                                itemsAndCats.length > 0 &&
                                !itemsAndCats.find(c => c.id === tile.categoryId)
                            ) {
                                return false
                            }
                            return true
                        }),
                    })),
                })),
            }))
            const changedLayouts = getChangedLayouts(
                newLayouts,
                oldLayouts,
                getSelectedSchematizedResolution(deps.getState())
            )
            const auditData = constructAuditData(deps, true)
            const deletedLayouts = oldLayouts.filter(l => !newLayouts.find(nl => nl.layoutId === l.layoutId))
            return [
                ...changedLayouts.map(l =>
                    schematizedSaveMutationRequested({
                        locationId: locationId,
                        layoutId: l.layoutId,
                        name: l.name,
                        sortIndex: l.sortIndex,
                        data: l.data,
                        size: getSelectedSchematizedResolution(deps.getState()),
                        auditData: auditData,
                    })
                ),
                ...deletedLayouts.map(l =>
                    schematizedLayoutDeleteMutationRequested({
                        locationId: locationId,
                        layoutId: l.layoutId,
                    })
                ),
            ]
        })
    )
}

export const schematizedSaveMutationRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) => {
    return actions$.pipe(
        ofType(SCHEMATIZED_SAVE_MUTATION_REQUESTED),
        switchMap((action: Action) => {
            return defer(() => schematizedSaveApi(getHttpHeaders(deps.getState()), action.payload.saveData)).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Schematized Data saved successfully")
                    return [schematizedSaveSuccess()]
                }),
                catchError(error => {
                    openNotification(NotificationType.Error, "Could not save the data, please try again later...")
                    logger.error("schematizedSaveMutationRequestedEpics - error", error)
                    return of(schematizedSaveFailure())
                })
            )
        })
    )
}

export const schematizedLayoutDeleteMutationRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) => {
    return actions$.pipe(
        ofType(SCHEMATIZED_LAYOUT_DELETE_MUTATION_REQUESTED),
        switchMap((action: Action) => {
            return defer(() =>
                schematizedLayoutDeleteApi(getHttpHeaders(deps.getState()), action.payload.deleteData)
            ).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Schematized Data saved successfully")
                    return [schematizedSaveSuccess()]
                }),
                catchError(error => {
                    openNotification(NotificationType.Error, "Could not delete the data, please try again later...")
                    logger.error("schematizedLayoutDeleteMutationRequestedEpics - error", error)
                    return of(schematizedSaveFailure())
                })
            )
        })
    )
}

export const enterFolderClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ENTER_FOLDER_CLICKED),
        switchMap((action: Action) => {
            return [schematizedGridSelected(action.payload.gridId)]
        })
    )

export const cancelButtonClickedClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_CANCEL_BUTTON_CLICKED),
        switchMap(() => {
            const layouts = getSchematizedLayouts(deps.getState())
            return [schematizedLayoutSelected(getFirstLayoutId(layouts))]
        })
    )

export const deleteSchematizedTileClickedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_SCHEMATIZED_TILE_REQUESTED),
        switchMap((action: Action) => {
            const actions: Action[] = [deleteSchematizedTile(action.payload.tileId)]

            // If the tile is a folder/category type tile, delete the linked group
            if (action.payload.targetPageGroupId) {
                const linkedGroup = getCurrentSchematizedGroups(deps.getState()).find(
                    g => g.id === action.payload.targetPageGroupId
                )

                if (linkedGroup) {
                    // Get all folder/category type tiles from the linked group
                    // and recursively delete all their linked groups
                    const groupFolderTiles = linkedGroup.pages.reduce(
                        (acc, page) => [...acc, ...page.tiles.filter(t => !!t.targetPageGroupId)],
                        []
                    )

                    actions.push(
                        deleteSchematizedGridGroup(linkedGroup.id),
                        ...groupFolderTiles.map(t => deleteSchematizedTileRequested(t.id, t.targetPageGroupId))
                    )
                }
            }

            return actions
        })
    )

export const addSchematizedTileEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ADD_NEW_SCHEMATIZED_TILE_REQUESTED),
        switchMap((action: Action) => {
            const categoriesData = getItemsAndCategoriesListResponse(deps.getState())
            const schematizedGroups = getCurrentSchematizedGroups(deps.getState())
            const schematizedUiData = getSchematizedComponentData(deps.getState())
            const updatedSchematizedGroups = getUpdatedSchematizedGroups(
                action.payload.newTile,
                schematizedGroups,
                categoriesData,
                schematizedUiData
            )
            return [updateSchematizedGroups(updatedSchematizedGroups)]
        })
    )

export const addSchematizedPageEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ADD_NEW_PAGE_REQUESTED),
        switchMap(() => {
            const newGridId = uuid.v4()
            const newGrid: SchematizedPage = {
                id: newGridId,
                tiles: [],
            }
            const currentGridGroup = getSchematizedSelectedGridGroup(deps.getState())

            if (!currentGridGroup) {
                logger.error("addSchematizedPageEpics - no currentGridGroup")
                return []
            }

            return [addSchematizedGrid(newGrid, currentGridGroup.id), schematizedGridSelected(newGridId)]
        })
    )

export const deleteSchematizedPageEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_CURRENT_PAGE_REQUESTED),
        switchMap(() => {
            const currentGridId = getSchematizedSelectedGridId(deps.getState())
            const currentGridGroup = getSchematizedSelectedGridGroup(deps.getState())

            if (!currentGridId || !currentGridGroup) {
                logger.error("deleteSchematizedPageEpics - no currentGridId || currentGridGroup")
                return []
            }

            const currentPageIdx = currentGridGroup.pages.findIndex(p => p.id === currentGridId)
            const selectedNextPage = currentGridGroup.pages[currentPageIdx > 0 ? currentPageIdx - 1 : 1]

            if (!selectedNextPage) {
                logger.error("deleteSchematizedPageEpics - no selectedNextPage")
                return []
            }

            return [deleteSchematizedGrid(currentGridId), schematizedGridSelected(selectedNextPage.id)]
        })
    )

export const addSchematizedLayoutEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ADD_NEW_TAB_CLICKED),
        switchMap(() => {
            const lastLayout = last(getSchematizedLayouts(deps.getState()))
            const newLayoutId = uuid.v4()
            const name = "New Tab"
            const data: SchematizedData = {
                layoutId: newLayoutId,
                name: "New Tab",
                sortIndex: lastLayout ? lastLayout.sortIndex + 1 : 0,
                size: getSelectedSchematizedResolution(deps.getState()),
                data: [
                    {
                        id: uuid.v4(),
                        isRoot: true,
                        pages: [
                            {
                                id: uuid.v4(),
                                tiles: [],
                            },
                        ],
                    },
                ],
            }
            return [
                addSchematizedLayout(data),
                schematizedLayoutSelected(newLayoutId),
                schematizedRenameLayoutValueChanged(name),
            ]
        })
    )

export const renameConfirmedSchematizedLayoutEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_LAYOUT_RENAME_CONFIRMED),
        switchMap(() => {
            const currentLayoutId = getSchematizedSelectedLayoutId(deps.getState())
            const newName = getSchematizedRenameLayoutValue(deps.getState())

            if (!currentLayoutId || !newName) {
                logger.error("renameSchematizedLayoutEpics - no currentLayoutId or newName")
                return []
            }

            return [
                updateSchematizedLayout(currentLayoutId, {
                    name: newName || "New Tab",
                }),
                schematizedRenameLayoutValueReset(),
            ]
        })
    )

export const renameClickedSchematizedLayoutEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SCHEMATIZED_LAYOUT_RENAME_CLICKED),
        switchMap(() => {
            const currentLayoutData = getSchematizedSelectedLayoutData(deps.getState())

            if (!currentLayoutData) {
                logger.error("renameClickedSchematizedLayoutEpics - no currentLayoutData")
                return []
            }

            return [schematizedRenameLayoutValueChanged(currentLayoutData.name)]
        })
    )

export const deleteSchematizedLayoutEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_TAB_CLICKED),
        switchMap(() => {
            const currentLayoutId = getSchematizedSelectedLayoutId(deps.getState())

            if (!currentLayoutId) {
                logger.error("deleteSchematizedLayoutEpics - no currentLayoutId")
                return []
            }

            const layouts = getSchematizedLayouts(deps.getState())
            const currentLayoutIdx = layouts.findIndex(l => l.layoutId === currentLayoutId)
            const nextLayout = layouts[currentLayoutIdx > 0 ? currentLayoutIdx - 1 : 1]

            if (!nextLayout) {
                logger.error("deleteSchematizedLayoutEpics - no nextLayoutId")
                return []
            }

            return [deleteSchematizedLayout(currentLayoutId), schematizedLayoutSelected(nextLayout.layoutId)]
        })
    )

export default [
    fetchSchematizedRequestedEpics,
    fetchSchematizedEpics,
    schematizedCustomFolderCloseClickEpics,
    schematizedMarkAsStickyClickedEpics,
    schematizedMoveTileEpics,
    deleteSchematizedTileClickedEpics,
    addSchematizedTileEpics,
    enterFolderClickedEpics,
    schematizedSaveButtonClickedEpics,
    cancelButtonClickedClickedEpics,
    customFolderUpdatedEpics,
    saveSchematizedStartLoading,
    saveSchematizedEndLoading,
    addSchematizedPageEpics,
    deleteSchematizedPageEpics,
    schematizedUpdatePagesRequestedEpics,
    addSchematizedLayoutEpics,
    deleteSchematizedLayoutEpics,
    renameConfirmedSchematizedLayoutEpics,
    renameClickedSchematizedLayoutEpics,
    schematizedSaveMutationRequestedEpics,
    schematizedLayoutDeleteMutationRequestedEpics,
]
